<template>
  <div class="text-xs-center" v-if="loading">
    <v-progress-circular indeterminate color="primary"></v-progress-circular>
  </div>
  <div class="chart-container" v-else>
    <span class="chart-guide"><img src="../assets/images/stacked-py-chart-guide.png" /></span>
    <GChart
      type="ColumnChart"
      :settings="chartSettings"
      :data="data"
      :options="chartOptions"
      :resizeDebounce="1000"
      :events="chartEvents"
      :createChart="(el, google) => new google.charts.Bar(el)"
      @ready="onChartReady"
      ref="gChart"
    />
  </div>
</template>

<script>
import {GChart} from 'vue-google-charts';

export default {
  name: "StackedBarWithPriorYear",
  data() {
    return {
      chartsLib: null,
      maxScale: 0,
      chartSettings: {
        packages: ['bar']
      },
      chartEvents: {
        'select': () => {
          const table = this.$refs.gChart.chartObject;
          const selection = table.getSelection()[0];

          if (selection !== undefined) {
            const row = selection.row + 1;

            const element = {};
            element.viewType = this.$store.state.Dashboard.widgetConfig.viewType;
            element.name = this.chartData[row][0];

            this.$emit('bar-selected', element);
          }
        }
      }
    }
  },
  props: ['chartData', 'loading', 'stackedType', 'chartVAxFormat', 'bus', 'dimensions'],
  components: {GChart},
  created() {
    this.data = this.chartData;

    if (this.chartVAxFormat !== 'percent') {
      const dataValues = [...this.chartData];

      dataValues.shift();

      const maxValues = [];

      let half;
      let pyHalf;
      let cyHalf;

      dataValues.forEach((collection) => {
        let mapCollection = [];

        for (let i = 0; i < collection.length; i++) {
          if (typeof collection[i] !== "string" && typeof collection[i] !== "object") {
            mapCollection.push(collection[i]);
          }
        }

        half = Math.ceil(mapCollection.length / 2);
        pyHalf = mapCollection.slice(0, half);
        cyHalf = mapCollection.slice(half);

        if (pyHalf.length > 0) {
          maxValues.push(pyHalf.reduce((sum, val) => sum + val));
        }
        if (cyHalf.length > 0) {
          maxValues.push(cyHalf.reduce((sum, val) => sum + val));
        }
      });

      const maxVal = Math.max(...maxValues);

      this.maxScale = Math.ceil(maxVal);
    } else {
      delete this.options.vAxes[0].viewWindow;
      delete this.options.vAxes[1].viewWindow;
    }

    this.bus.$on('tab-changed', () => {
      console.log(this.$refs.gChart);

      const refChart = this.$refs.gChart;
      const chartObject = refChart.chartObject;

      setTimeout(function () {
        chartObject.clearChart();
        refChart.drawChart();
      }, 1);
    });
  },
  methods: {
    onChartReady(chart, google) {
      this.chartsLib = google;
    }
  },
  computed: {
    chartOptions() {
      if (!this.chartsLib) return null;
      return this.chartsLib.charts.Bar.convertOptions(this.options);
    },
    options() {
      return {
        isStacked: this.stackedType ? this.stackedType : true,
        seriesType: 'bars',
        series: {
          0: {                                       // Converted
            color: '#e15735',
            targetAxisIndex: 0,
            visibleInLegend: false,
          },
          1: {                                       // Quote Accepted
            targetAxisIndex: 0,
            color: '#517dd4',
            visibleInLegend: false,
          },
          2: {                                       // Quote Rejected
            targetAxisIndex: 0,
            color: '#c34d4d',
            visibleInLegend: false,
          },
          3: {                                       // Awaiting Acceptance
            targetAxisIndex: 0,
            color: '#7db726',
            visibleInLegend: false,
          },
          4: {                                       // Requote Requested
            targetAxisIndex: 0,
            color: '#e2608b',
            visibleInLegend: false,
          },
          5: {                                       // Quote Sent - Not Followed Up
            targetAxisIndex: 0,
            color: '#26a8ce',
            visibleInLegend: false,
          },
          6: {                                       // Quote Completed By Design Team
            targetAxisIndex: 0,
            color: '#a826a8',
            visibleInLegend: false,
          },
          7: {                                       // Requirements Sent to Design Team
            targetAxisIndex: 0,
            color: '#34a63a',
            visibleInLegend: false,
          },
          8: {                                       // Gathering Customer Requirements and Quote Prep
            targetAxisIndex: 0,
            color: '#ffa826',
            visibleInLegend: false,
          },
          9: {                                       // Lead Cancelled
            targetAxisIndex: 0,
            color: '#e15735',
            visibleInLegend: false,
          },
          10: {                                       // New Lead
            targetAxisIndex: 0,
            color: '#517dd4',
            visibleInLegend: false,
          },
          11: {                                        // Converted
            targetAxisIndex: 1,
            color: '#dc3912',
          },
          12: {                                        // Quote Accepted
            targetAxisIndex: 1,
            color: '#3366cc',
          },
          13: {                                        // Quote Rejected
            targetAxisIndex: 1,
            color: '#b82e2e',
          },
          14: {                                        // Awaiting Acceptance
            targetAxisIndex: 1,
            color: '#66aa00',
          },
          15: {                                        // Requote Requested
            targetAxisIndex: 1,
            color: '#dd4477',
          },
          16: {                                        // Quote Sent - Not Followed Up
            targetAxisIndex: 1,
            color: '#0099c6',
          },
          17: {                                        // Quote Completed By Design Team
            targetAxisIndex: 1,
            color: '#990099',
          },
          18: {                                        // Requirements Sent to Design Team
            targetAxisIndex: 1,
            color: '#109618',
          },
          19: {                                        // Gathering Customer Requirements and Quote Prep
            targetAxisIndex: 1,
            color: '#ff9900',
          },
          20: {                                        // Lead Cancelled
            targetAxisIndex: 1,
            color: '#dc3912',
          },
          21: {                                       // New Lead
            targetAxisIndex: 1,
            color: '#3366cc',
          }
        },
        tooltip: {isHtml: true},
        theme: 'maximized',
        chartArea: {
          width: '100%',
          left: 250
        },
        annotations: {
          alwaysOutside: true
        },
        height: 500,
        forceIFrame: true,
        bar: {
          groupWidth: '75%'
        },
        legend: {
          width: "20%",
          textStyle: {
            fontSize: 12,
            fontName: 'Arial'
          }
        },
        hAxis: {
          format: '',
          title: '',
        },
        vAxis: {
          textStyle: {
            fontSize: 11.2
          }
        },
        vAxes: {
          0: {
            viewWindow: {
              max: this.maxScale
            },
            format: this.chartVAxFormat
          },
          1: {
            viewWindow: {
              max: this.maxScale
            },
            gridlines: {
              color: 'transparent',
            },
            format: this.chartVAxFormat
          }
        },
        // Colours are ordered from the bottom up as they are rendered on the chart
        colors: [
          "#3366cc", // Converted
          "#dc3912", // Quote Accepted
          "#ff9900", // Quote Rejected
          "#109618", // Awaiting Acceptance
          "#990099", // Requote Requested
          "#0099c6", // Quote Sent - Not Followed Up
          "#dd4477", // Gathering Customer Requirements
          "#66aa00", // Lead Cancelled
          "#b82e2e" // New Lead
        ]
      }
    }
  },
  watch: {
    chartData: function (data) {
      this.data = data;
    }
  }
}
</script>

<style scoped>
svg:not(:root) {
  overflow: visible;
}

.chart-container {
  position: relative;
}

.chart-guide {
  position: absolute;
  bottom: 10px;
  right: 130px;
  z-index: 10;
}

.chart-guide img {
  height: 140px;
}
</style>
